import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import style from "./AviationChangeAmountModal.module.scss";
import { Aviation } from "../../../../services";
import { CURRENCIES } from "../../../../mock";
import CustomButton from "../../../custom/custom-button/CustomButton";
import CustomInput from "../../../custom/custom-input/CustomInput";
import CustomInputCurrency from "../../../custom/custom-input-currency/CustomInputCurrency";
import CustomTextarea from "../../../custom/custom-textarea/CustomTextarea";
import CloseIconNew from "../../../shared/icons/CloseIconNew";
import { useTenderActionError } from "../../../../hooks/useTenderActionError";

const formatPrice = (value, currency) => {
  if (value === null || value === undefined || value === "") return "—";
  const symbol = CURRENCIES[currency]?.symbol || currency;
  return `${Number(value).toLocaleString("ru-RU")} ${symbol}`;
};

const AviationChangeAmountModal = ({ isOpen, onClose, order, extraCost, onSuccess, onRefreshOrder }) => {
  const { t } = useTranslation();
  const { requestErrorDisplay } = useTenderActionError(order, { onRefreshOrder });
  const [price, setPrice] = useState("");
  const [reason, setReason] = useState("");
  const [priceError, setPriceError] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen || !order) return null;

  const currency =
    extraCost?.currency || order.acceptedCurrency || order.currency || "EUR";
  const currentPrice = extraCost?.price ?? order.price;

  const resetAndClose = () => {
    setPrice("");
    setReason("");
    setPriceError("");
    setReasonError("");
    onClose?.();
  };

  const handleSave = async () => {
    const roundedPrice = Math.round(Number(price));
    const trimmedReason = reason.trim();

    if (!roundedPrice || roundedPrice <= 0) {
      setPriceError(t("orderInfoExpeditor.editAmount.correctAmountRequired"));
      return;
    }
    if (roundedPrice > 2000000000) {
      setPriceError(t("orderInfoExpeditor.editAmount.amountTooLarge"));
      return;
    }
    if (!trimmedReason) {
      const message = t("orderInfoExpeditor.editAmount.reasonRequired");
      setReasonError(message);
      toast.error(message);
      return;
    }

    const payload = {
      price: roundedPrice,
      comment: trimmedReason,
      currency,
    };

    try {
      setIsLoading(true);
      await Aviation.updateAirTenderPrice(order.id, payload);
      toast.success(t("orderInfoExpeditor.editAmount.success"));
      setIsLoading(false);
      onSuccess?.();
      resetAndClose();
    } catch (error) {
      console.error(error);
      await requestErrorDisplay(error, t, "orderInfoExpeditor.editAmount.error");
      setIsLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div className={style.overlay} onClick={resetAndClose} data-portal-modal="true">
      <div className={style.modal} role="dialog" onClick={(event) => event.stopPropagation()}>
        <div className={style.header}>
          <span className={style.title}>{t("orderInfoExpeditor.editAmount.title")}</span>
          <button
            type="button"
            className={style.close}
            onClick={resetAndClose}
            aria-label={t("orderInfoExpeditor.editAmount.close")}
          >
            <CloseIconNew width="24" height="24" fill="#212121" />
          </button>
        </div>

        <div className={style.body}>
          <CustomInput
            isDisabled
            isBgGray
            inputType="text"
            labelText={t("orderInfoExpeditor.editAmount.currentBid")}
            value={formatPrice(currentPrice, currency)}
          />
          <CustomInputCurrency
            isBgGray
            lockCurrency
            inputType="number"
            labelText={t("orderInfoExpeditor.editAmount.newBidRequired")}
            placeholder={t("orderInfoExpeditor.editAmount.newBidPlaceholder")}
            currency={currency}
            value={price}
            errorText={priceError}
            onChangeValue={(value) => {
              setPrice(value);
              setPriceError("");
            }}
          />
          <CustomTextarea
            isBgGray
            rows={2}
            labelText={t("orderInfoExpeditor.editAmount.reasonRequiredLabel")}
            placeholder={t("orderInfoExpeditor.editAmount.reasonPlaceholder")}
            value={reason}
            errorText={reasonError}
            onChangeValue={(value) => {
              setReason(value);
              setReasonError("");
            }}
          />
        </div>

        <div className={style.footer}>
          <CustomButton
            text={t("common.cancel")}
            isNavy
            isWhite
            onClick={resetAndClose}
            disabled={isLoading}
          />
          <CustomButton
            text={t("common.save")}
            isNavy
            onClick={handleSave}
            isLoading={isLoading}
            disabled={isLoading}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AviationChangeAmountModal;
